/**
 * Vorlage „Ofen vorheizen“.
 *
 * Der Knopf wird auf die Heizart gedreht, die Kontrolllampe leuchtet, die
 * Heizstäbe glühen auf, und die große Anzeige zählt bis zur Zieltemperatur.
 * Ist sie erreicht, erscheint ein Häkchen: Jetzt kann das Backgut hinein.
 */
import { clamp, ease, lerp, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { roundedRectPath } from '../paths.ts';
import { compact, faded, group, line, rect, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { heatWaves } from '../parts/effects.ts';
import { HEATING_MODES, OVEN, OVEN_WINDOW, ovenParts } from '../parts/oven.ts';
import type { HeatingMode } from '../parts/oven.ts';
import { arcArrow, doneMark } from '../parts/stage.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Zeitplan. */
const TIME = {
  knob: [0.02, 0.12],
  heat: [0.12, 0.74],
  done: [0.76, 0.8],
  fade: [0.95, 1],
} as const;

/** Heizstab als Zickzack über die Breite des Fensters. */
function rod(y: number, glow: number): SceneNode {
  const x0 = OVEN_WINDOW.x + 12;
  const x1 = OVEN_WINDOW.x + OVEN_WINDOW.width - 12;
  const count = 9;
  let d = `M${x0} ${y}`;
  for (let index = 1; index <= count; index += 1) {
    d += `L${lerp(x0, x1, index / count)} ${y + (index % 2 === 0 ? 0 : 3)}`;
  }
  return group([
    shape(d, { stroke: 'deep', strokeWidth: STROKE.outline }),
    shape(d, { stroke: 'accent', strokeWidth: STROKE.outline, opacity: glow }),
  ]);
}

/** Lüfterrad hinten im Ofen. */
function fan(t: number, power: number): SceneNode {
  const cx = OVEN_WINDOW.x + OVEN_WINDOW.width / 2;
  const cy = OVEN_WINDOW.y + OVEN_WINDOW.height / 2;
  const blades: SceneNode[] = [];
  for (let index = 0; index < 4; index += 1) {
    blades.push(
      shape(`M${cx} ${cy}Q${cx + 9} ${cy - 3} ${cx + 11} ${cy + 3}Q${cx + 5} ${cy + 4} ${cx} ${cy}Z`, {
        fill: 'shade',
        stroke: 'ink',
        strokeWidth: STROKE.fine,
      }),
    );
  }
  const angle = t * 360 * 6 * power;
  return group([
    { kind: 'circle', cx, cy, r: 15, fill: 'none', stroke: 'deep', strokeWidth: STROKE.detail },
    group(
      blades.map((blade, index) => group([blade], { transform: { rotate: index * 90, ox: cx, oy: cy } })),
      {
        transform: { rotate: angle, ox: cx, oy: cy },
      },
    ),
  ]);
}

/** Vorlage „Ofen vorheizen“. */
export const preheatOvenTemplate = defineTemplate({
  id: 'bake.preheat',
  group: 'bake',
  title: 'Ofen vorheizen',
  description:
    'Der Backofen wird auf Heizart und Temperatur eingestellt. Die Anzeige steigt, bis die Temperatur erreicht ist.',
  durationSeconds: 6,
  keywords: [
    'vorheizen',
    'backofen',
    'ofen auf',
    'umluft',
    'ober unterhitze',
    'oberhitze',
    'unterhitze',
    'grad',
  ],
  stillTime: 0.85,
  parameters: [
    {
      kind: 'number',
      name: 'temperature',
      label: 'Temperatur',
      min: 50,
      max: 250,
      step: 10,
      unit: '°C',
      defaultValue: 180,
    },
    choice('mode', 'Heizart', HEATING_MODES, 'topBottom'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const values: Record<string, string | number> = {};
    const match = /(\d{2,3})\s*(?:°|grad)/.exec(lower);
    if (match?.[1] !== undefined) {
      values.temperature = Number(match[1]);
    }
    if (lower.includes('umluft')) values.mode = 'convection';
    else if (lower.includes('grill')) values.mode = 'grill';
    else if (lower.includes('unterhitze') && !lower.includes('ober')) values.mode = 'bottom';
    else if (lower.includes('ober')) values.mode = 'topBottom';
    return values;
  },
  scene(values, t) {
    const target = numberValue(values, 'temperature', 180);
    const mode = textValue(values, 'mode', 'topBottom') as HeatingMode;
    const knob = ease.inOutCubic(segment(t, ...TIME.knob));
    const fade = ease.inOutCubic(segment(t, ...TIME.fade));
    const heating = knob > 0.5 && fade < 0.5;
    const heat = ease.outCubic(segment(t, ...TIME.heat)) * (1 - fade);
    const temperature = Math.round(lerp(20, target, heat) / 5) * 5;
    const glow = heating ? 0.35 + 0.65 * heat * (0.92 + 0.08 * wave(t, 6)) : 0;
    const { body, door } = ovenParts({
      temperature: heating ? Math.max(20, temperature) : 20,
      mode,
      heating,
      knobAngle: lerp(0, 135, knob) * (1 - fade),
    });

    const windowPath = roundedRectPath(
      OVEN_WINDOW.x,
      OVEN_WINDOW.y,
      OVEN_WINDOW.width,
      OVEN_WINDOW.height,
      7,
    );
    const top = mode === 'topBottom' || mode === 'grill' || mode === 'convection';
    const bottom = mode === 'topBottom' || mode === 'bottom' || mode === 'convection';
    // Innenraum dunkel; das Glühen breitet sich von den Heizstäben aus.
    const glowBand = (y: number): SceneNode =>
      rect(OVEN_WINDOW.x, y - 9, OVEN_WINDOW.width, 18, 0, { fill: 'accentTint', stroke: 'none' });
    const inside = group(
      compact([
        rect(OVEN_WINDOW.x, OVEN_WINDOW.y, OVEN_WINDOW.width, OVEN_WINDOW.height, 0, {
          fill: 'cavity',
          stroke: 'none',
        }),
        faded(heating ? heat * 0.55 : 0, [
          top ? glowBand(OVEN_WINDOW.y + 8) : null,
          bottom ? glowBand(OVEN_WINDOW.y + OVEN_WINDOW.height - 8) : null,
        ]),
        // Einschubleisten
        ...[0.38, 0.66].map((f) =>
          line(
            OVEN_WINDOW.x + 4,
            OVEN_WINDOW.y + OVEN_WINDOW.height * f,
            OVEN_WINDOW.x + OVEN_WINDOW.width - 4,
            OVEN_WINDOW.y + OVEN_WINDOW.height * f,
            { stroke: 'deep', strokeWidth: STROKE.detail },
          ),
        ),
        mode === 'convection' ? fan(t, heating ? 1 : 0) : null,
        top ? rod(OVEN_WINDOW.y + 7, glow) : null,
        bottom ? rod(OVEN_WINDOW.y + OVEN_WINDOW.height - 10, glow) : null,
      ]),
      { clip: windowPath },
    );

    const reached = segment(t, ...TIME.done) * (1 - fade);
    return compact([
      ...heatWaves(OVEN.x + OVEN.width / 2, OVEN.y - 2, t, heating ? heat * 0.9 : 0, 3, 34),
      ...body,
      inside,
      ...door,
      faded(clamp(ease.outBack(reached) * 1.2), [
        // Neben der Bedienleiste, frei vom Ofen
        doneMark(OVEN.x + OVEN.width + 19, OVEN.y + OVEN.panel / 2 + 1, 1.2),
      ]),
    ]);
  },
  hints() {
    const knob = { x: OVEN.x + OVEN.width - 20, y: OVEN.y + OVEN.panel / 2 + 1 };
    return [arcArrow(knob.x, knob.y, 15, 15, 200, 330)];
  },
});
